// IMPORTANT: THis file is used on frontend and backend, so do not include
// things here that do not need to be part of the frontend. Instead, use
// the front-end/back-end specific utils files.
import { t } from 'i18next';
import { safeParseInt } from './utils';

export interface IVideoParams {
  autoPlay?: boolean;
  mute?: boolean;
  loop?: boolean;
  start?: number;
  end?: number;
  controls?: boolean;
}

export enum TVideoType {
  Youtube = 'youtube',
  Twitch = 'twitch'
  // Douyu = 'douyu'
}

export const enum TVideoTrackType {
  MPlus = 'video_mplus',
  Raid = 'video_raid'
}

export const enum TVideoLogType {
  User = 'user',
  System = 'system'
}

export enum TEventVideoEntityType {
  Collection = 'collection',
  Event = 'event'
}

export enum TEventVideoTags {
  HallOfFame = 'hall-of-fame',
  Trailer = 'trailer',
  Highlights = 'highlights',
  HubStandings = 'hub-standings'
}

export function getVideoEmbedLink(videoId: string, platform: TVideoType, videoParams: IVideoParams = {}): string {
  const queryParams: string[] = [];
  if (platform === TVideoType.Twitch) {
    queryParams.push(`parent=${RIO_SITE.external_hostname}`);
  }
  if (videoParams.autoPlay) {
    if (platform === TVideoType.Youtube) {
      queryParams.push('autoplay=1');
    } else if (platform === TVideoType.Twitch) {
      queryParams.push('autoplay=true');
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push('autoplay=true');
    // }
  }

  if (!videoParams.autoPlay) {
    if (platform === TVideoType.Youtube) {
      queryParams.push('autoplay=0');
    } else if (platform === TVideoType.Twitch) {
      queryParams.push('autoplay=false');
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push('autoplay=true');
    // }
  }

  if (videoParams.mute) {
    if (platform === TVideoType.Youtube) {
      queryParams.push('mute=1');
    } else if (platform === TVideoType.Twitch) {
      queryParams.push('muted=true');
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push('muted=true');
    // }
  }

  if (videoParams.loop) {
    if (platform === TVideoType.Youtube) {
      queryParams.push('loop=1');
    } else if (platform === TVideoType.Twitch) {
      queryParams.push('loop=true');
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push('loop=true');
    // }
  }

  if (videoParams.start) {
    if (platform === TVideoType.Youtube) {
      queryParams.push(`start=${videoParams.start}`);
    } else if (platform === TVideoType.Twitch) {
      queryParams.push(`t=${videoParams.start}s`);
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push(`start=${videoParams.start}`);
    // }
  }

  if (videoParams.end) {
    if (platform === TVideoType.Youtube) {
      queryParams.push(`end=${videoParams.end}`);
    } else if (platform === TVideoType.Twitch) {
      queryParams.push(`t=${videoParams.end}s`);
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push(`end=${videoParams.end}`);
    // }
  }

  if (videoParams.controls) {
    if (platform === TVideoType.Youtube) {
      queryParams.push('controls=1');
    } else if (platform === TVideoType.Twitch) {
      queryParams.push('controls=true');
    }
    // else if (platform === TVideoType.Douyu) {
    //   queryParams.push('controls=true');
    // }
  }

  if (platform === TVideoType.Youtube) {
    queryParams.push(`origin=${encodeURIComponent('http://raider.io')}`);
  }

  let queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

  switch (platform) {
    case TVideoType.Youtube:
      return `https://www.youtube.com/embed/${videoId}${queryString}`;
    case TVideoType.Twitch:
      queryString = queryParams.length > 0 ? `&${queryParams.join('&')}` : '';
      return `https://player.twitch.tv/?video=v${videoId}${queryString}`;
    // case TVideoType.Douyu:
    //   return `https://www.douyu.com/room/v${videoId}${queryString}`;
    default:
      return '';
  }
}

export const validatePrefilledVideo = (url: string, withTimestamp: boolean) => {
  const parsedUrl = parseVideoLink(url, withTimestamp);
  if (!parsedUrl) {
    return t('videos:errors.url-parse-error');
  }
  return null;
};

interface VideoLink {
  platform: TVideoType;
  videoId: string;
  timestamp?: number;
}

interface IVideoRegex {
  urlRegex: RegExp;
  timestampRegex: RegExp;
}

const PLATFORMS: Record<TVideoType, IVideoRegex> = {
  [TVideoType.Youtube]: {
    urlRegex: /(?:youtube\.com\/watch\?v=([\w-]+)|youtu\.be\/([\w-]+)|youtube\.com\/live\/([\w-]+))/i,
    timestampRegex: /[?&]t=(\d+)s?/i
  },
  [TVideoType.Twitch]: {
    urlRegex: /twitch\.tv\/videos\/(\d+)/i,
    timestampRegex: /[?&]t=((\d+)h)?((\d+)m)?((\d+)s)?/i
  }
};

function getTimestampSecondsFromVideoURL(url: string, platform: TVideoType): number | null {
  const timestampRegex = PLATFORMS[platform].timestampRegex;
  const match = url.match(timestampRegex);

  if (match) {
    switch (platform) {
      case TVideoType.Youtube:
        return safeParseInt(match[1]);
      case TVideoType.Twitch:
        const hours = safeParseInt(match[2]);
        const minutes = safeParseInt(match[4]);
        const seconds = safeParseInt(match[6]);
        return hours * 3600 + minutes * 60 + seconds;
      default:
        return null;
    }
  }

  return null;
}

export function getTimestampFromAnyPlatform(url: string): number | null {
  const platform = getVideoPlatformFromURL(url);
  if (platform) {
    const timestamp = getTimestampSecondsFromVideoURL(url, platform);
    if (!!timestamp) {
      return timestamp;
    }
  }

  return null;
}

function getVideoPlatformFromURL(url: string): TVideoType | null {
  for (const platform in PLATFORMS) {
    if (PLATFORMS[platform as TVideoType].urlRegex.test(url)) {
      return platform as TVideoType;
    }
  }
  return null;
}

export function parseVideoLink(link: string, withTimestamp: boolean = false): VideoLink | null {
  for (const platformKey in PLATFORMS) {
    const platform = platformKey as TVideoType;
    const match = link.match(PLATFORMS[platform].urlRegex);

    if (match) {
      const videoId = match[1] || match[2] || match[3];
      let timestamp: number | null = 0;

      if (withTimestamp) {
        timestamp = getTimestampSecondsFromVideoURL(link, platform);
        if (!timestamp) {
          return null;
        }
      }

      return { platform, videoId, timestamp };
    }
  }

  return null;
}

export function convertTwitchDurationToSeconds(duration: string): number {
  let totalSeconds = 0;

  const hours = duration.match(/(\d+)h/i);
  if (hours) {
    totalSeconds += parseInt(hours[1], 10) * 3600;
  }

  const minutes = duration.match(/(\d+)m/i);
  if (minutes) {
    totalSeconds += parseInt(minutes[1], 10) * 60;
  }

  const seconds = duration.match(/(\d+)s/i);
  if (seconds) {
    totalSeconds += parseInt(seconds[1], 10);
  }

  return totalSeconds;
}

export function getVideoLink(videoId: string, platform: TVideoType, timestamp?: number | null): string {
  let url: string;

  switch (platform) {
    case TVideoType.Youtube:
      url = `https://www.youtube.com/watch?v=${videoId}`;
      if (timestamp) {
        url += `&t=${timestamp}s`;
      }
      break;
    case TVideoType.Twitch:
      url = `https://www.twitch.tv/videos/${videoId}`;
      if (timestamp) {
        url += `?t=${timestamp}s`;
      }
      break;
    // case TVideoType.Douyu:
    //   url = `https://www.douyu.com/${videoId}`;
    //   if (timestamp) {
    //     url += `?t=${timestamp}`;
    //   }
    //   break;
    default:
      url = '';
  }

  return url;
}
